import { useEffect, useRef, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Bot, Filter, ImagePlus, Loader2, Paperclip, Search, Send, ShieldAlert, Sparkles, Trash2, User, X } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { useLang } from "@/hooks/useLang";
import { useAuth } from "@/hooks/useAuth";
import { renderMarkdown } from "@/lib/renderMarkdown";
import ChatHeader from "./smart-chat/ChatHeader";
import CallingScreen from "./smart-chat/CallingScreen";
import { useAgoraRoom } from "@/hooks/useAgoraRoom";
import { measureAction } from "@/lib/sentry";

interface Message {
  role: "user" | "assistant";
  content: string;
  translated?: string;
  attachments?: string[];
  id?: string;
  created_at?: string;
}

type MessageFilter = "all" | "user" | "assistant";

const SmartChat = () => {
  const { languages } = useLang();
  const { user } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [messages, setMessages] = useState<Message[]>([
    { role: "assistant", content: "Welcome to The Floor AI. I can help you prepare speeches, refine debate strategy, analyze rhetoric, or coach your delivery. How can I assist you?" },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [translateTo, setTranslateTo] = useState("");
  const [autoTranslate, setAutoTranslate] = useState(false);
  const [attachments, setAttachments] = useState<string[]>([]);
  const [calling, setCalling] = useState<{ active: boolean; isVideo: boolean }>({ active: false, isVideo: false });
  const [filter, setFilter] = useState<MessageFilter>("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [showSearch, setShowSearch] = useState(false);

  const agora = useAgoraRoom();

  // Load messages from DB on mount
  useEffect(() => {
    if (!user) return;
    const loadMessages = async () => {
      const { data, error } = await supabase
        .from("smart_chat_messages")
        .select("*")
        .eq("user_id", user.id)
        .order("created_at", { ascending: true })
        .limit(100);
      if (data && data.length > 0) {
        setMessages(data.map((m: any) => ({
          id: m.id,
          role: m.role as "user" | "assistant",
          content: m.content,
          attachments: m.attachments || [],
          created_at: m.created_at,
        })));
      }
    };
    loadMessages();
  }, [user]);

  // Real-time subscription for new messages
  useEffect(() => {
    if (!user) return;
    const channel = supabase
      .channel("smart-chat-realtime")
      .on("postgres_changes", {
        event: "INSERT",
        schema: "public",
        table: "smart_chat_messages",
        filter: `user_id=eq.${user.id}`,
      }, (payload: any) => {
        const m = payload.new;
        setMessages(prev => {
          if (prev.some(p => p.id === m.id)) return prev;
          return [...prev, {
            id: m.id,
            role: m.role,
            content: m.content,
            attachments: m.attachments || [],
            created_at: m.created_at,
          }];
        });
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [user]);

  useEffect(() => {
    const handler = () => setOpen(true);
    window.addEventListener("open-smart-chat", handler);
    return () => window.removeEventListener("open-smart-chat", handler);
  }, []);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages]);

  const startCall = async (isVideo: boolean) => {
    setCalling({ active: true, isVideo });
    try {
      await agora.join(`smart-chat-${Date.now()}`);
    } catch (error: any) {
      toast({ title: "Call failed", description: error.message, variant: "destructive" });
      setCalling({ active: false, isVideo: false });
    }
  };

  const hangUp = async () => {
    await agora.leave();
    setCalling({ active: false, isVideo: false });
  };

  const persistMessage = useCallback(async (role: string, content: string, messageAttachments?: string[]) => {
    if (!user) return;
    await supabase.from("smart_chat_messages").insert({
      user_id: user.id,
      role,
      content,
      attachments: messageAttachments || [],
    });
  }, [user]);

  const translateAssistantMessage = async (messageIndex: number, text: string) => {
    if (!translateTo) return;
    try {
      const targetLabel = languages.find((item) => item.code === translateTo)?.label || translateTo;
      const { data, error } = await supabase.functions.invoke("ai-writer-tool", {
        body: { text, action: "smart-chat-translate", prompt: `Translate this response into ${targetLabel}. Preserve tone and formatting. Return only the translated response.` },
      });
      if (error) throw error;
      setMessages((prev) => prev.map((message, index) => (index === messageIndex ? { ...message, translated: data?.result || text } : message)));
    } catch (error: any) {
      toast({ title: "Translation error", description: error.message, variant: "destructive" });
    }
  };

  const sendMessage = () => measureAction("smart_chat.send", async () => {
    if ((!input.trim() && attachments.length === 0) || loading) return;
    const preparedInput = input.trim() || "Please analyze the attached media context.";
    const composedPrompt = attachments.length ? `${preparedInput}\n\nAttached media filenames: ${attachments.join(", ")}` : preparedInput;
    const userMsg: Message = { role: "user", content: preparedInput, attachments };
    setMessages((prev) => [...prev, userMsg]);
    setInput("");
    const sentAttachments = [...attachments];
    setAttachments([]);
    setLoading(true);

    // Persist user message
    persistMessage("user", preparedInput, sentAttachments);

    try {
      const { data, error } = await supabase.functions.invoke("ai-writer-tool", {
        body: {
          text: composedPrompt, action: "smart-chat",
          prompt: "You are The Floor AI — a strategic communications assistant for elite public speakers, heads of state, CEOs, governors, and debate professionals. Be concise, authoritative, and tactical. Help with speech writing, rhetorical strategy, debate preparation, delivery coaching, audience analysis, and crisis communication. If media filenames are provided, acknowledge them and explain what kind of oratory or strategic help you can offer around that media context.",
        },
      });
      if (error) throw error;
      const responseText = data?.result || "I couldn't generate a response.";
      setMessages((prev) => {
        const next = [...prev, { role: "assistant", content: responseText } as Message];
        if (autoTranslate && translateTo) window.setTimeout(() => translateAssistantMessage(next.length - 1, responseText), 0);
        return next;
      });
      // Persist assistant message
      persistMessage("assistant", responseText);
    } catch (error: any) {
      toast({ title: "Chat error", description: error.message, variant: "destructive" });
      setMessages((prev) => [...prev, { role: "assistant", content: "Sorry, I encountered an error. Please try again." }]);
    } finally {
      setLoading(false);
    }
  });

  const handleFiles = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(event.target.files ?? []).map((file) => file.name).filter(Boolean);
    if (!selected.length) return;
    setAttachments((prev) => [...prev, ...selected].slice(-4));
    event.target.value = "";
  };

  const clearHistory = async () => {
    if (!user) return;
    await supabase.from("smart_chat_messages").delete().eq("user_id", user.id);
    setMessages([{ role: "assistant", content: "Chat history cleared. How can I assist you?" }]);
    toast({ title: "History cleared" });
  };

  // Filtered messages
  const filteredMessages = messages.filter((msg) => {
    if (filter !== "all" && msg.role !== filter) return false;
    if (searchQuery && !msg.content.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  });

  if (!open) {
    return (
      <motion.button initial={{ scale: 0 }} animate={{ scale: 1 }} onClick={() => setOpen(true)}
        className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-sovereign-gold text-background flex items-center justify-center shadow-lg hover:shadow-sovereign-gold/30 transition-shadow">
        <Sparkles className="w-6 h-6" />
      </motion.button>
    );
  }

  return (
    <>
      <AnimatePresence>{calling.active && <CallingScreen contactName="The Floor AI" isVideo={calling.isVideo} onHangUp={hangUp} />}</AnimatePresence>

      <motion.div
        initial={{ opacity: 0, y: 20, scale: 0.95 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 20, scale: 0.95 }}
        className={`fixed z-50 bg-card border border-border rounded-2xl shadow-2xl flex flex-col overflow-hidden transition-all duration-200 ${
          expanded ? "bottom-4 right-4 left-4 top-20 md:left-auto md:w-[620px] md:top-16" : "bottom-6 right-6 w-[400px] h-[540px]"
        }`}
      >
        <ChatHeader
          expanded={expanded} onToggleExpand={() => setExpanded((p) => !p)} onClose={() => setOpen(false)}
          onVoiceCall={() => startCall(false)} onVideoCall={() => startCall(true)}
          isVerified={true} translateTo={translateTo} onTranslateChange={setTranslateTo}
          autoTranslate={autoTranslate} onAutoTranslateToggle={() => setAutoTranslate((p) => !p)} languages={languages}
        />

        {/* Filter & Search Bar */}
        <div className="px-3 py-1.5 border-b border-border flex items-center gap-1.5">
          <div className="flex gap-1">
            {(["all", "user", "assistant"] as MessageFilter[]).map((f) => (
              <button key={f} onClick={() => setFilter(f)}
                className={`px-2 py-0.5 rounded-full text-[10px] font-medium transition-colors ${
                  filter === f ? "bg-sovereign-gold/20 text-sovereign-gold border border-sovereign-gold/30" : "text-muted-foreground hover:text-foreground border border-transparent"
                }`}>
                {f === "all" ? "All" : f === "user" ? "You" : "AI"}
              </button>
            ))}
          </div>
          <div className="flex-1" />
          <button onClick={() => setShowSearch(p => !p)} className="w-6 h-6 rounded-full flex items-center justify-center text-muted-foreground hover:text-foreground">
            <Search className="w-3 h-3" />
          </button>
          <button onClick={clearHistory} className="w-6 h-6 rounded-full flex items-center justify-center text-muted-foreground hover:text-destructive" title="Clear history">
            <Trash2 className="w-3 h-3" />
          </button>
        </div>

        {showSearch && (
          <div className="px-3 py-1.5 border-b border-border">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-muted-foreground" />
              <input value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Search messages…"
                className="w-full bg-secondary/50 rounded-full pl-7 pr-7 py-1.5 text-[11px] text-foreground placeholder:text-muted-foreground outline-none focus:ring-1 focus:ring-sovereign-gold/40" />
              {searchQuery && (
                <button onClick={() => setSearchQuery("")} className="absolute right-2 top-1/2 -translate-y-1/2">
                  <X className="w-3 h-3 text-muted-foreground" />
                </button>
              )}
            </div>
          </div>
        )}

        {/* Messages */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3">
          {filteredMessages.map((msg, index) => (
            <motion.div key={msg.id || `${msg.role}-${index}`} initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }}
              className={`flex gap-2 ${msg.role === "user" ? "justify-end" : ""}`}>
              {msg.role === "assistant" && (
                <div className="w-6 h-6 rounded-full bg-ai-cyan/10 border border-ai-cyan/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Bot className="w-3 h-3 text-ai-cyan" />
                </div>
              )}
              <div className={`max-w-[82%] rounded-xl px-3 py-2 text-xs leading-relaxed ${
                msg.role === "user" ? "bg-sovereign-gold/15 text-foreground border border-sovereign-gold/20" : "bg-secondary/50 text-foreground border border-border"
              }`}>
                <div className="whitespace-pre-wrap">{msg.role === "assistant" ? renderMarkdown(msg.content) : msg.content}</div>
                {!!msg.attachments?.length && (
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {msg.attachments.map((a) => (
                      <span key={a} className="rounded-full border border-border bg-background/60 px-2 py-0.5 text-[10px] text-muted-foreground">{a}</span>
                    ))}
                  </div>
                )}
                {msg.translated && (
                  <div className="mt-2 rounded-lg border border-ai-cyan/15 bg-ai-cyan/5 px-2.5 py-2 text-[11px] text-ai-cyan">{msg.translated}</div>
                )}
              </div>
              {msg.role === "user" && (
                <div className="w-6 h-6 rounded-full bg-sovereign-gold/10 border border-sovereign-gold/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <User className="w-3 h-3 text-sovereign-gold" />
                </div>
              )}
            </motion.div>
          ))}
          {loading && (
            <div className="flex gap-2">
              <div className="w-6 h-6 rounded-full bg-ai-cyan/10 border border-ai-cyan/20 flex items-center justify-center flex-shrink-0">
                <Bot className="w-3 h-3 text-ai-cyan" />
              </div>
              <div className="bg-secondary/50 rounded-xl px-3 py-2 border border-border">
                <Loader2 className="w-3.5 h-3.5 animate-spin text-ai-cyan" />
              </div>
            </div>
          )}
        </div>

        {/* Input */}
        <div className="p-3 border-t border-border space-y-2">
          {attachments.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {attachments.map((a) => (
                <button key={a} onClick={() => setAttachments((prev) => prev.filter((i) => i !== a))}
                  className="rounded-full border border-border bg-secondary/40 px-2 py-1 text-[10px] text-muted-foreground hover:text-foreground"
                >{a} ×</button>
              ))}
            </div>
          )}
          <div className="flex items-center gap-2">
            <input ref={fileInputRef} type="file" accept="image/*,video/*,audio/*" multiple className="hidden" onChange={handleFiles} />
            <button type="button" onClick={() => fileInputRef.current?.click()} className="w-8 h-8 rounded-full border border-border bg-secondary/40 text-muted-foreground flex items-center justify-center hover:text-foreground" aria-label="Upload media">
              <Paperclip className="w-3.5 h-3.5" />
            </button>
            <button type="button" onClick={() => fileInputRef.current?.click()} className="w-8 h-8 rounded-full border border-border bg-secondary/40 text-muted-foreground flex items-center justify-center hover:text-foreground" aria-label="Add image or video">
              <ImagePlus className="w-3.5 h-3.5" />
            </button>
            <button type="button" onClick={() => startCall(true)} className="w-8 h-8 rounded-full border border-destructive/40 bg-destructive/10 text-destructive flex items-center justify-center hover:bg-destructive/20 transition-colors" aria-label="Emergency Support" title="Emergency Live Agent">
              <ShieldAlert className="w-3.5 h-3.5" />
            </button>
            <input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && sendMessage()}
              placeholder="Ask about speeches, debate strategy, rhetoric…" className="flex-1 bg-secondary/50 rounded-full px-4 py-2 text-xs text-foreground placeholder:text-muted-foreground outline-none focus:ring-1 focus:ring-sovereign-gold/40" disabled={loading} />
            <button onClick={sendMessage} disabled={loading || (!input.trim() && attachments.length === 0)}
              className="w-8 h-8 rounded-full bg-sovereign-gold text-background flex items-center justify-center disabled:opacity-50 hover:bg-sovereign-gold/80 transition-colors"
            ><Send className="w-3.5 h-3.5" /></button>
          </div>
        </div>
      </motion.div>
    </>
  );
};

export default SmartChat;
